/**
 * Company identity + the compliance notice our payment partner's onboarding asks us to show.
 *
 * Their message names exactly two things: the company name as evidence of website ownership,
 * and the required policies — Terms and Conditions, Privacy Policy, Refund Policy, About Us,
 * Contact Us. `LEGAL_NOTICE` below is that message in our own voice; nothing else belongs here.
 *
 * Read by: the app shell footer (every page), the public /legal page, and the vendor profile's
 * Legal row. Never inline the company name or the notice in a template.
 */

export const COMPANY = {
  /** Name shown in the site-wide © line. */
  displayName: 'Inspiration Valley',
  /** Registered name, as on the SSM certificate — used where the operator is identified in full. */
  legalName: 'Inspiration Valley Enterprise',
  /** Business registration number. TODO: fill from the SSM certificate — shown only when set. */
  ssm: '',
  brand: 'Trexa',
  addressLines: ['F-2-4, Plaza Damas 1, Taman Sri Hartamas', '50480 Kuala Lumpur, Malaysia'],
  email: 'sales@ivalley.my',
  phone: '+60 11-6202 9002',
  /** Digits-only, for wa.me / tel: links. */
  phoneDigits: '601162029002',
} as const;

/** The site-wide footer line: "© 2026 Inspiration Valley. All rights reserved." */
export function copyrightLine(): string {
  return `© ${new Date().getFullYear()} ${COMPANY.displayName}. All rights reserved.`;
}

/**
 * The compliance notice, in the wording of our payment partner's onboarding message — flipped
 * from "your company / your business" to our own voice. It heads the legal page; `LEGAL_POLICIES`
 * below carries the five policies it names.
 */
export const LEGAL_NOTICE = {
  title: 'Legal & policies',
  /** Evidence of website ownership — the company name, stated plainly. */
  ownership: `This website belongs to ${COMPANY.legalName}${COMPANY.ssm ? `, SSM ${COMPANY.ssm}` : ''}.`,
  policiesIntro: 'This website includes the required information and policies:',
  policiesTail: 'and any other relevant policies applicable to the nature of our business.',
  note: 'The above information is provided to fulfil our onboarding and compliance requirements.',
} as const;

export interface LegalPolicy {
  key: string;
  title: string;
  /** One-line summary, shown on the collapsed row. */
  sub: string;
  /** Paragraphs of the policy itself. */
  body: string[];
}

/**
 * The five required policies, in the order the onboarding message lists them. Each is kept to a
 * few short paragraphs — long enough to actually be a policy, short enough to read on a phone.
 */
export const LEGAL_POLICIES: readonly LegalPolicy[] = [
  {
    key: 'terms',
    title: 'Terms and Conditions',
    sub: 'The agreement between you and Trexa',
    body: [
      `${COMPANY.brand} is an online event-services marketplace operated by ${COMPANY.legalName}${COMPANY.ssm ? ` (SSM ${COMPANY.ssm})` : ''}, registered in Malaysia at ${COMPANY.addressLines.join(', ')}. By creating an account, listing a service, or making or accepting a booking, you agree to these Terms.`,
      `Services are supplied by the vendor, not by ${COMPANY.brand}. ${COMPANY.brand} provides the booking record, the invoice, the payment escrow and the dispute process, and is not a party to the service contract between a customer and a vendor.`,
      'You must give accurate registration details and keep your password confidential. Vendor accounts are verified against business documents before they can take bookings, and we may suspend an account that fails verification, breaches these Terms or is used unlawfully.',
      'A booking becomes binding when the vendor issues an invoice and the customer pays the deposit stated on it. Changes to a confirmed booking take effect only when both the vendor and the customer accept them in the app.',
      `${COMPANY.brand} is not liable for the quality, timing or legality of a service supplied by a vendor; our liability for any claim connected with a booking is limited to the amount we hold or have handled for that booking. Nothing here excludes liability that cannot lawfully be excluded.`,
      'We may update these Terms, and material changes are notified in the app or by email before they take effect. These Terms are governed by the laws of Malaysia.',
    ],
  },
  {
    key: 'privacy',
    title: 'Privacy Policy',
    sub: 'What we collect and how we use it',
    body: [
      `${COMPANY.legalName} is the data user responsible for personal data processed on ${COMPANY.brand}, in line with the Personal Data Protection Act 2010.`,
      'We collect account details (name, email, phone, password; for vendors also business name, address, category and registration documents), booking details (event date, venue, guest count, services, invoices and payment records), payout details for vendors, and technical log data.',
      'We use it to create and secure your account, process bookings, invoices, payments and payouts, verify vendor businesses, provide support, send booking notifications, prevent fraud, and meet our legal and tax obligations.',
      'We share only what is needed: with the other party to your booking, with our licensed payment partner and our messaging and hosting providers, and with regulators or law enforcement where the law requires. We do not sell your personal data. Card details are handled by our payment partner and are never stored on our servers.',
      `Booking, invoice and payment records are kept for at least seven years to meet Malaysian accounting requirements; other account data is kept while your account is active. To request a copy of your data, correct it, or withdraw consent, write to ${COMPANY.email} and we will respond within 21 days.`,
    ],
  },
  {
    key: 'refund',
    title: 'Refund Policy',
    sub: 'Cancellations, refunds and how money moves',
    body: [
      `Every customer payment on ${COMPANY.brand} is held in escrow until the event is delivered, so a cancellation before completion is refunded from escrow directly.`,
      'A booking cancelled before the event date is refunded from the escrow balance held for it, calculated on the amount actually paid. The refund figure is shown to both the customer and the vendor before the cancellation is confirmed. If a vendor cancels a confirmed booking, the customer is refunded in full, including the deposit.',
      'If an accepted change reduces a booking, the reduction is applied to the outstanding balance; any amount already held above the revised total is refunded or issued as a credit note against the same booking.',
      'Once an event is marked complete, escrow is released to the vendor and the booking is no longer refundable through the platform. Complaints about a completed event should be raised with us within 7 days of the event date.',
      `Refunds are returned to the original payment method through our payment partner and normally clear within 5 – 14 working days. If a customer and vendor cannot agree, either may raise a dispute in the app: escrow for that booking is frozen while we review it, and we decide within 14 working days. For help with a dispute, contact ${COMPANY.email}.`,
    ],
  },
  {
    key: 'about',
    title: 'About Us',
    sub: `${COMPANY.brand}, operated by ${COMPANY.legalName}`,
    body: [
      `${COMPANY.brand} is owned and operated by ${COMPANY.legalName}${COMPANY.ssm ? ` (SSM ${COMPANY.ssm})` : ''}, a Malaysian company based in Kuala Lumpur at ${COMPANY.addressLines.join(', ')}.`,
      `${COMPANY.brand} is an event-services marketplace for Malaysia. Customers plan a wedding or majlis in one place — hall, catering, photography, decoration and attire — and vendors run their bookings, invoices and payouts from a single dashboard.`,
      'Event deposits are paid months ahead and both sides carry risk, so customer money sits in escrow until the event is delivered: a customer is not left chasing a deposit, and a vendor is not left unpaid after the work is done. Vendors are verified against their business registration and payout account before they can take a booking.',
    ],
  },
  {
    key: 'contact',
    title: 'Contact Us',
    sub: 'How to reach the company behind Trexa',
    body: [
      COMPANY.legalName,
      ...COMPANY.addressLines,
      `Email: ${COMPANY.email}`,
      `Phone / WhatsApp: ${COMPANY.phone}`,
      'Support hours: Monday – Friday, 9:00am – 6:00pm (MYT)',
    ],
  },
];
